import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { useContext, useState } from "react";
import { updatePassword } from "../services/UsersService";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { AuthContext } from "../utils/AuthContext";
import { useNavigate } from "react-router-dom";
import { logout } from "../services/AuthService";

const UpdatePassword = () => {
  const { auth, setAuth } = useContext(AuthContext);

  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');


  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [newPasswordConfirmError, setNewPasswordConfirmError] = useState('');

  const [passwordCriteria, setPasswordCriteria] = useState({
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasMinLength: false,
  });

  const [isFocused, setIsFocused] = useState(false);

  const [isLoading, setLoading] = useState(false);


  function validateForm() {
    let isValid = true;

    setCurrentPasswordError('');
    setNewPasswordError('');
    setNewPasswordConfirmError('');

    const current_passwowrd = currentPassword.trim();
    const new_password = newPassword.trim();
    const new_password_confirm = newPasswordConfirm.trim();

    if (current_passwowrd === '') {
      setCurrentPasswordError('Enter your current password.');

      isValid = false;
    }

    if (new_password === '') {
      setNewPasswordError('Enter your new password.');

      isValid = false;
    } else if (!passwordCriteria.hasUpperCase || !passwordCriteria.hasLowerCase
      || !passwordCriteria.hasNumber || !passwordCriteria.hasSpecialChar
      || !passwordCriteria.hasMinLength
    ) {
      setNewPasswordError('Your password is not strong.');

      isValid = false;
    } else if (new_password === current_passwowrd) {
      setNewPasswordError('Your new password should be the same as your current password.');

      isValid = false;
    }

    if (new_password_confirm === '') {
      setNewPasswordConfirmError('Enter your new password confirmation.');

      isValid = false;
    } else if (new_password !== new_password_confirm) {
      setNewPasswordConfirmError('Passwords are not the same.');

      isValid = false;
    }



    return isValid;
  }


  const validateNewPassword = (password) => {
    // Regex checks for each individual condition
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[@$!%*?&#]/.test(password);
    const hasMinLength = password.length >= 6;

    // Update state based on criteria
    setPasswordCriteria({
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChar,
      hasMinLength,
    });
  };

  const handleNewPasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    validateNewPassword(password);
  };


  const renderIcon = (isValid) => (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {isValid ? (
        <CheckIcon sx={{ color: "#3328BF" }} />
      ) : (
        <CheckIcon sx={{ color: "#9e9e9e" }} />
      )}
    </Box>
  );

  const handleFocus = () => {
    setIsFocused(true); // Set focused state to true when the input is focused
  };

  const handleBlur = () => {
    setIsFocused(false); // Set focused state to false when the input loses focus
  };


  function handleUpdatePassword(event) {
    event.preventDefault();

    const validated = validateForm();

    if (!validated) {
      return;
    }

    setLoading(true);

    updatePassword({
      token: auth.token, data: {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: newPasswordConfirm
      }
    }).then(response => {
      toast.success("Password has been updated", {
        autoClose: 2000,
        hideProgressBar: true,
        icon: ({ theme, type }) => <CheckIcon sx={{ color: '#3328BF' }} />,
        closeButton: false
      });


      // clear inputs
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');

      setLoading(false);

      // logout then redirect to login page
      handleLogout();
    }).catch(err => {
      setLoading(false);

      const result = displayErrors(err.response.data);

      if (!result) {
        toast.error(err.response.data.message, {
          autoClose: 2000,
          hideProgressBar: true,
          closeButton: false
        });
      }



    });
  }

  function displayErrors(data) {
    if (data.hasOwnProperty('errors')) {
      if (data.errors.hasOwnProperty('current_password')) {
        setCurrentPasswordError(data.errors.current_password);
      }

      if (data.errors.hasOwnProperty('new_password')) {
        setNewPasswordError(data.errors.new_password);
      }

      if (data.errors.hasOwnProperty('new_password_confirmation')) {
        setNewPasswordConfirmError(data.errors.new_password_confirmation);
      }
    } else {
      return false;
    }


    return true;
  }

  function handleLogout() {
    localStorage.removeItem('agent-connect-api-token');
    localStorage.removeItem('agent-connect-current-user');

    logout({ token: auth.token });

    setAuth({
      token: null,
      user: null
    });

    navigate('/login');
  }


  return (
    <Box>
      <Typography variant="h5" component="div">
        Update Password
      </Typography>
      <Typography variant="p" component="div" sx={{ color: '#9e9e9e' }}>
        Secure your account by regularly updating your password
      </Typography>

      <form onSubmit={handleUpdatePassword}>
        <Box sx={{ mt: 3 }}>
          {/* <label for="current_password" style={{ fontWeight: 600, color: '#212121' }}>Current password</label> */}
          <TextField
            label="Current password"
            type="password"
            name="Current Password"
            id="current_password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            fullWidth
            error={currentPasswordError !== ''}
            helperText={currentPasswordError}
            placeholder="Enter your current password"
            variant="filled"
            sx={{
              '& .MuiFilledInput-root': {
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                '&.Mui-focused': {
                  boxShadow: '0 0 5px rgba(51, 40, 191, 0.4)',
                },
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              },
            }}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          {/* <label for="new_password" style={{ fontWeight: 600, color: '#212121' }}>New password</label> */}
          <TextField
            label="New password"
            type="password"
            name="New Password"
            id="new_password"
            value={newPassword}
            onChange={handleNewPasswordChange}
            fullWidth
            onFocus={handleFocus} // Trigger when input is focused
            onBlur={handleBlur} // Trigger when input loses focus
            error={newPasswordError !== ''}
            helperText={newPasswordError}
            placeholder="Enter your new password"
            variant="filled"
            sx={{
              '& .MuiFilledInput-root': {
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                '&.Mui-focused': {
                  boxShadow: '0 0 5px rgba(51, 40, 191, 0.4)',
                },
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              },
            }}
          />

          {isFocused && ( // Only show criteria when the TextField is focused
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                {renderIcon(passwordCriteria.hasUpperCase)}
                <span style={{ marginLeft: 8 }}>At least one uppercase letter</span>
              </Typography>
              <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                {renderIcon(passwordCriteria.hasLowerCase)}
                <span style={{ marginLeft: 8 }}>At least one lowercase letter</span>
              </Typography>
              <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                {renderIcon(passwordCriteria.hasNumber)}
                <span style={{ marginLeft: 8 }}>At least one number</span>
              </Typography>
              <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                {renderIcon(passwordCriteria.hasSpecialChar)}
                <span style={{ marginLeft: 8 }}>At least one special character</span>
              </Typography>
              <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                {renderIcon(passwordCriteria.hasMinLength)}
                <span style={{ marginLeft: 8 }}>At least 6 characters</span>
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          {/* <label for="confirm_new_password" style={{ fontWeight: 600, color: '#212121' }}>Confirm new password</label> */}
          <TextField
            label="Confirm new password"
            type="password"
            name="Confirm New Password"
            id="confirm_new_password"
            value={newPasswordConfirm}
            onChange={(e) => setNewPasswordConfirm(e.target.value)}
            fullWidth
            error={newPasswordConfirmError !== ''}
            helperText={newPasswordConfirmError}
            placeholder="Enter your new password confirmation"
            variant="filled"
            sx={{
              '& .MuiFilledInput-root': {
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                '&.Mui-focused': {
                  boxShadow: '0 0 5px rgba(51, 40, 191, 0.4)',
                },
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mt: 4 }}>
          <Button
            disabled={isLoading}
            type="submit"
            variant="contained"
            sx={{ textTransform: 'none' }}
            size="large"
          >
            Change Password
          </Button>

          {
            isLoading &&
            <CircularProgress size={24} sx={{ ml: 2 }} />
          }
        </Box>
      </form>
    </Box>
  );
}

export default UpdatePassword;
